import { useMemo } from "react";

/**
 * SectionFX — a small, dependency-free animation framework to make any section,
 * header, card header or hero feel lively. Pure CSS keyframes (defined in
 * index.css, prefixed `fx*`). Respects prefers-reduced-motion.
 *
 * Building blocks:
 *   <AnimatedBackdrop variant="aurora|grid|blobs|sheen" colors={[...]} />
 *   <ParticleField variant="sparkles|coins|bubbles|confetti|petals" count density />
 *   <AnimatedText variant="shimmer|wave|pop|rgb"> text </AnimatedText>
 *   <SectionFX backdrop particles> ...children... </SectionFX>  (convenience wrapper)
 *
 * All are absolutely-positioned & pointer-events-none except AnimatedText.
 */

// ── deterministic pseudo-random so SSR/hydration is stable ──
function rng(seed) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => (s = (s * 16807) % 2147483647) / 2147483647;
}

const PARTICLES = {
  sparkles: { glyph: "✦", anims: ["fxTwinkle"], sizes: [10, 20] },
  coins:    { glyph: "●", anims: ["fxFloatUp", "fxSpin"], sizes: [10, 16], gold: true },
  bubbles:  { glyph: "○", anims: ["fxFloatUp"], sizes: [8, 22] },
  confetti: { glyph: "▬", anims: ["fxFall"], sizes: [6, 12], confetti: true },
  petals:   { glyph: "❁", anims: ["fxDrift", "fxFall"], sizes: [10, 18] },
};
const CONFETTI_COLORS = ["#f43f5e", "#f59e0b", "#22c55e", "#3b82f6", "#a855f7"];

export function ParticleField({ variant = "sparkles", count = 16, color = "#ffffff", seed = 7, className = "" }) {
  const cfg = PARTICLES[variant] || PARTICLES.sparkles;
  const nodes = useMemo(() => {
    const r = rng(seed + count);
    return Array.from({ length: count }).map((_, i) => {
      const size = cfg.sizes[0] + r() * (cfg.sizes[1] - cfg.sizes[0]);
      const anim = cfg.anims[Math.floor(r() * cfg.anims.length)];
      return {
        left: `${r() * 100}%`, top: `${r() * 100}%`, size,
        dur: `${5 + r() * 8}s`, delay: `${-r() * 8}s`, anim,
        col: cfg.confetti ? CONFETTI_COLORS[i % CONFETTI_COLORS.length] : (cfg.gold ? "#fbbf24" : color),
        op: 0.35 + r() * 0.5,
      };
    });
  }, [variant, count, color, seed]); // eslint-disable-line
  return (
    <div aria-hidden className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} data-fx="particles">
      {nodes.map((n, i) => (
        <span key={i} style={{
          position: "absolute", left: n.left, top: n.top, fontSize: n.size, lineHeight: 1,
          color: n.col, opacity: n.op, animation: `${n.anim} ${n.dur} ease-in-out ${n.delay} infinite`,
          textShadow: "0 1px 2px rgba(0,0,0,.12)",
        }}>{cfg.glyph}</span>
      ))}
    </div>
  );
}

export function AnimatedBackdrop({ variant = "aurora", colors = ["#6043EF", "#0ea5e9"], className = "" }) {
  const [c1, c2] = colors;
  if (variant === "grid") {
    return (
      <div aria-hidden className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} data-fx="backdrop">
        <div className="absolute inset-x-[-25%] bottom-[-60%] h-[180%]" style={{
          transform: "perspective(340px) rotateX(58deg)", transformOrigin: "bottom",
          backgroundImage: "linear-gradient(rgba(255,255,255,.14) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.14) 1px,transparent 1px)",
          backgroundSize: "26px 26px",
        }} />
      </div>
    );
  }
  if (variant === "sheen") {
    return (
      <div aria-hidden className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} data-fx="backdrop">
        <span className="absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-18deg] bg-white/20" style={{ animation: "fxSweepX 6s linear infinite" }} />
      </div>
    );
  }
  // aurora / blobs
  return (
    <div aria-hidden className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} data-fx="backdrop">
      <div className="absolute -top-24 left-[6%] w-72 h-72 rounded-full blur-3xl" style={{ background: c1, opacity: 0.3, animation: "fxAurora 12s ease-in-out infinite" }} />
      <div className="absolute -bottom-28 right-[8%] w-80 h-80 rounded-full blur-3xl" style={{ background: c2, opacity: 0.25, animation: "fxAurora 15s ease-in-out infinite reverse" }} />
    </div>
  );
}

export function AnimatedText({ variant = "shimmer", children, className = "", as: Tag = "span" }) {
  const text = typeof children === "string" ? children : "";
  if (variant === "wave" || variant === "pop") {
    const anim = variant === "wave" ? "fxWave" : "fxPop";
    const dur = variant === "wave" ? "1.8s" : "0.6s";
    const inf = variant === "wave" ? "infinite" : "1";
    return (
      <Tag className={className} data-fx="text">
        {[...text].map((ch, i) => (
          <span key={i} style={{ display: "inline-block", animation: `${anim} ${dur} ease ${i * 0.05}s ${inf} both`, whiteSpace: "pre" }}>
            {ch}
          </span>
        ))}
      </Tag>
    );
  }
  const cls = variant === "rgb" ? "fx-rgb" : "fx-shimmer";
  return <Tag className={`${cls} ${className}`} data-fx="text">{children}</Tag>;
}

export function SectionFX({ backdrop, backdropColors, particles, particleCount = 16, particleColor = "#ffffff", seed = 7, className = "", children }) {
  return (
    <div className={`relative ${className}`}>
      {backdrop && <AnimatedBackdrop variant={backdrop} colors={backdropColors} />}
      {particles && <ParticleField variant={particles} count={particleCount} color={particleColor} seed={seed} />}
      <div className="relative z-10">{children}</div>
    </div>
  );
}
